import React, { useState, useEffect, useRef } from 'react';
import { getUnreadNotificationCount } from '../services/notificationService';
import { getChannel } from '../services/channelService';
import NotificationList from './NotificationList';
import './NotificationBadge.css';

const NotificationBadge = ({ currentUser, onNavigateToPost, selectedChannel }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;

    console.log('NotificationBadge: Setting up for user:', currentUser.email);
    setError('');

    const unsubscribe = getUnreadNotificationCount(currentUser.email, (count) => {
      console.log('NotificationBadge: Unread count:', count);
      setUnreadCount(count);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNotificationClick = async (notification) => {
    try {
      console.log('NotificationBadge: Handling notification click:', notification);
      
      // Get the channel for this notification
      const channel = await getChannel(notification.channelId);
      
      // Switch to the correct channel
      if (onNavigateToPost) {
        onNavigateToPost(channel);
      }
      
      // Close the dropdown
      setShowNotifications(false);
      
      // Scroll to the specific post/reply after a short delay to allow channel switch
      setTimeout(() => {
        let targetElement;
        
        if (notification.contentType === 'post') {
          // For post notifications, scroll to the post
          targetElement = document.getElementById(`post-${notification.contentId}`);
        } else {
          // For reply notifications, scroll to the reply within the post
          targetElement = document.getElementById(`reply-${notification.contentId}`);
          
          // If reply not found, scroll to the parent post and expand replies
          if (!targetElement) {
            targetElement = document.getElementById(`post-${notification.postId}`);
            
            // Try to expand replies if they're collapsed
            if (targetElement) {
              const replyButton = targetElement.querySelector('.replies-view-button');
              if (replyButton && !replyButton.textContent.includes('Hide')) {
                replyButton.click();
                
                // Try again to find the reply after expanding
                setTimeout(() => {
                  const replyElement = document.getElementById(`reply-${notification.contentId}`);
                  if (replyElement) {
                    replyElement.scrollIntoView({ 
                      behavior: 'smooth', 
                      block: 'center' 
                    });
                    replyElement.style.backgroundColor = 'rgba(70, 29, 110, 0.1)';
                    setTimeout(() => {
                      replyElement.style.backgroundColor = '';
                    }, 3000);
                  }
                }, 300);
                return;
              }
            }
          }
        }
        
        if (targetElement) {
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          
          // Add a highlight effect
          targetElement.style.backgroundColor = 'rgba(70, 29, 110, 0.1)';
          setTimeout(() => {
            targetElement.style.backgroundColor = '';
          }, 3000);
        } else {
          console.log('NotificationBadge: Target element not found for notification:', notification);
        }
      }, 500);
      
    } catch (error) {
      console.error('NotificationBadge: Error handling notification click:', error);
      // Still close the dropdown even if navigation fails
      setShowNotifications(false);
    }
  };

  const toggleNotifications = () => {
    console.log('NotificationBadge: Toggle clicked, current state:', showNotifications);
    setShowNotifications(!showNotifications);
    console.log('NotificationBadge: New state will be:', !showNotifications);
  };

  return (
    <div className="notification-badge-container" ref={dropdownRef}>
      <button 
        className="notification-button"
        onClick={toggleNotifications}
        title="Notifications"
      >
        <span className="notification-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-count">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showNotifications && (
        <div className="notification-dropdown">
          <NotificationList 
            currentUser={currentUser}
            onNotificationClick={handleNotificationClick}
          />
        </div>
      )}

      {error && (
        <div className="notification-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default NotificationBadge;